/**
 * <SendEmailButton>
 * Reusable "send by email" trigger.
 *
 * Pattern: outline button → opens confirm dialog → optional override email →
 * calls the supplied async `onSend(to)` → toast feedback.
 *
 * Designed to drop into:
 *   - Invoice / quotation post-save flow
 *   - Customer statement report page
 *   - Future: PO / SO / delivery note pages
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Mail, Send, Loader2 } from 'lucide-react';

const SendEmailButton = ({
    onSend,
    defaultTo = '',
    label,
    title,
    description,
    successMessage,
    requireRecipient = false,
    testId = 'send-email-btn',
    variant = 'outline',
    size = 'sm',
    className = '',
    icon: Icon = Mail,
    disabled = false,
}) => {
    const { t } = useTranslation(['common']);
    const [open, setOpen] = useState(false);
    const [to, setTo] = useState(defaultTo);
    const [sending, setSending] = useState(false);

    const handleSend = async () => {
        if (requireRecipient && !to.trim()) {
            toast.error(t('common:toasts.email_required', { defaultValue: 'Recipient email required' }));
            return;
        }
        setSending(true);
        try {
            const result = await onSend(to.trim() || undefined);
            toast.success(successMessage || t('common:toasts.email_sent', { defaultValue: 'Email sent' }));
            setOpen(false);
            return result;
        } catch (err) {
            toast.error(err?.response?.data?.detail || t('common:toasts.email_failed', { defaultValue: 'Email failed' }));
        } finally {
            setSending(false);
        }
    };

    return (
        <>
            <Button
                type="button"
                variant={variant}
                size={size}
                disabled={disabled}
                onClick={() => { setTo(defaultTo); setOpen(true); }}
                data-testid={testId}
                className={className}
            >
                <Icon size={14} className="mr-2" />
                {label || t('common:actions.send_email', { defaultValue: 'Send by Email' })}
            </Button>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent data-testid={`${testId}-dialog`} className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>{title || t('common:actions.send_email', { defaultValue: 'Send by Email' })}</DialogTitle>
                        <DialogDescription>
                            {description || t('common:toasts.email_confirm', { defaultValue: 'Send this document by email.' })}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2 py-2">
                        <Label htmlFor={`${testId}-to`}>{t('common:fields.recipient', { defaultValue: 'Recipient email' })}</Label>
                        <Input
                            id={`${testId}-to`}
                            type="email"
                            value={to}
                            onChange={(e) => setTo(e.target.value)}
                            placeholder={requireRecipient ? 'accountant@example.com' : (defaultTo || 'customer@example.com')}
                            data-testid={`${testId}-input`}
                        />
                        {!requireRecipient && defaultTo && (
                            <p className="text-xs text-gray-500">
                                {t('common:toasts.email_default_hint', { defaultValue: 'Leave blank to use the customer email on file.' })}
                            </p>
                        )}
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={sending}>
                            {t('common:actions.cancel')}
                        </Button>
                        <Button
                            type="button"
                            onClick={handleSend}
                            disabled={sending}
                            data-testid={`${testId}-confirm`}
                            className="bg-[#1A1A1A] hover:bg-[#333] text-white"
                        >
                            {sending ? <Loader2 size={14} className="animate-spin mr-2" /> : <Send size={14} className="mr-2" />}
                            {t('common:actions.send', { defaultValue: 'Send' })}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default SendEmailButton;
